import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DomainService } from '../domain/domain.service';
import { WindowRef } from '../domain/window-ref';
import { WellKnownConfig } from './well-known-config';
import { finalize, first, firstValueFrom, Observable, shareReplay, tap, throwError } from 'rxjs';
import { SESSION_STATE, STATE, TOKEN_RESPONSE, VERIFIER, WELL_KNOWN_CONFIG } from './connect-storage-keys';
import { HOSTNAME } from '../domain/hostname';
import { Profile } from './profile';
import { ContextResponse } from './context-response';
import { AuthorizationResponse } from './authorization-response';
import { LAST_USED_TENANT_ID, LAST_USED_TENANT_NAME, WELCOME_DIALOG_SEEN } from './custom';
import { TokenResponse } from './token-response';
import CryptoJS from 'crypto-js';
import { PROFILE_ROLE } from './profile-role';
import { CONTEXT_TYPE } from './context-type';
import { PATH } from '../const/path';
import { ADMIN_UI_CURRENT_CONTEXT, ADMIN_UI_TOKEN_KIND, isProvisioningTokenKind } from './storage-keys';
import { Context } from './context';

/**
 * How much life the access token must have left before it is handed to an embedded web component.
 *
 * These components hold a token of their own and call the API directly, outside HttpClient - so the
 * interceptor's invisible 401 recovery does not cover them and a spent token surfaces to the user as
 * a dead session. 60 seconds because the assistant's library refuses anything within 30 seconds of
 * `exp` by its own reckoning; the rest is room for the request itself.
 */
const EMBEDDED_TOKEN_MIN_LIFETIME_MS = 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  private readonly httpClient = inject(HttpClient);
  private readonly domainService = inject(DomainService);
  private readonly windowRef = inject(WindowRef);

  /** The refresh currently in flight, shared by every caller of refreshAccessToken(). */
  private refreshInFlight: Observable<TokenResponse> | null = null;

  getWellKnownConfig(hostname: string): Observable<WellKnownConfig> {
    const config = this.domainService.getConnectIdentityConfig(hostname);
    return this.httpClient.get<WellKnownConfig>(config.wellknown_config);
  }

  getConnectLoginUrl(hostname: string, options?: { skipTenantHint?: boolean; provisioningToken?: boolean }): string {
    const wkcstr = localStorage.getItem(WELL_KNOWN_CONFIG);
    if (wkcstr) {
      const wellKonwnConfig: WellKnownConfig = JSON.parse(wkcstr);
      const config = this.domainService.getConnectIdentityConfig(hostname);
      const verifier = this.getVerifier();
      localStorage.setItem(VERIFIER, verifier);
      const state = this.getState();
      sessionStorage.setItem(STATE, state);
      const sha256 = this.getSha256(verifier);
      // Provisioning needs a user token that is NOT tenant-enabled: request the narrow provisioning scope and skip the tenant hint.
      const scope = options?.provisioningToken ? config.provisioningScope : config.scope;
      // Boolean short-circuit, not a nullish fallback: with `??` a `skipTenantHint` of `false` would be
      // returned as-is and `provisioningToken` never consulted. The rule cannot tell the two apart.
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      const skipTenantHint = options?.skipTenantHint || options?.provisioningToken;  // NOSONAR boolean short-circuit, not a nullish fallback
      let url = `${wellKonwnConfig.authorization_endpoint}?client_id=${config.client_id}&redirect_uri=${config.redirect_uri}&response_type=code&scope=${scope}&response_mode=query&state=${state}&code_challenge=${sha256}&code_challenge_method=S256`;
      if (!skipTenantHint) {
        const ctstr = sessionStorage.getItem(ADMIN_UI_CURRENT_CONTEXT);
        if (ctstr) {
          const context: Context = JSON.parse(ctstr);
          url = `${url}&tenant_hint=${context.contextId}`;
        }
      }
      return url;
    } else {
      throw new Error('No wellknown config in local storage!');
    }
  }

  getConnectLogoutUrl(hostname: string): string | null {
    const config = this.domainService.getConnectIdentityConfig(hostname);
    const wkcstr = localStorage.getItem(WELL_KNOWN_CONFIG);
    const trStr = sessionStorage.getItem(TOKEN_RESPONSE);
    sessionStorage.clear();
    localStorage.removeItem(VERIFIER);

    if (wkcstr) {
      const wellKnownConfig = JSON.parse(wkcstr);

      let token: TokenResponse;
      if (trStr) {
        token = JSON.parse(trStr);
      } else {
        return null;
      }
      return `${wellKnownConfig.end_session_endpoint}?id_token_hint=${token.id_token}&post_logout_redirect_uri=${config.post_logout_redirect_uri}`
    }
    return null;
  }

  getCheckSessionConfig(hostname: string): { iframeUrl: string; clientId: string; sessionState: string } | null {
    const wkcstr = localStorage.getItem(WELL_KNOWN_CONFIG);
    const sessionState = sessionStorage.getItem(SESSION_STATE);
    if (!wkcstr || !sessionState) {
      return null;
    }
    const wellKnownConfig: WellKnownConfig = JSON.parse(wkcstr);
    if (!wellKnownConfig.check_session_iframe) {
      return null;
    }
    const config = this.domainService.getConnectIdentityConfig(hostname);
    return {
      iframeUrl: wellKnownConfig.check_session_iframe,
      clientId: config.client_id,
      sessionState
    };
  }

  getTokenRequest(authResponse: AuthorizationResponse, hostname: string): Observable<TokenResponse> {
    const config = this.domainService.getConnectIdentityConfig(hostname);
    const state = this.getState();
    sessionStorage.setItem(STATE, state);

    if (state !== authResponse.state) {
      throw new Error('State changed!');
    }
    const verifier = localStorage.getItem(VERIFIER);
    let body = `grant_type=authorization_code&code=${authResponse.code}&client_id=${config.client_id}&redirect_uri=${config.redirect_uri}&code_verifier=${verifier}`;
    if (this.isEnvironmentMockedWithAws(hostname)) {
      const ctstr = sessionStorage.getItem(ADMIN_UI_CURRENT_CONTEXT);
      if (ctstr) {
        const context: Context = JSON.parse(ctstr);
        body = `${body}&tenant_hint=${context.contextId}`;
      }
    }
    const wkcstr = localStorage.getItem(WELL_KNOWN_CONFIG);

    if (wkcstr) {
      const wellKnownConfig: WellKnownConfig = JSON.parse(wkcstr);
      const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
      return this.httpClient.post<TokenResponse>(wellKnownConfig.token_endpoint, body, { headers: headers });
    } else {
      throw new Error('No wellknown config in local storage!');
    }
  }

  refreshToken(refreshToken: string): Observable<TokenResponse> {
    const wkcstr = localStorage.getItem(WELL_KNOWN_CONFIG);
    if (wkcstr) {
      const hostname = this.windowRef.getHostname();
      const config = this.domainService.getConnectIdentityConfig(hostname);
      const body = `client_id=${config.client_id}&grant_type=refresh_token&refresh_token=${refreshToken}`
      const wellKnownConfig: WellKnownConfig = JSON.parse(wkcstr);
      const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');
      return this.httpClient.post<TokenResponse>(wellKnownConfig.token_endpoint, body, { headers: headers });
    } else {
      throw new Error('No wellknown config in local storage!');
    }
  }

  // Refresh using the refresh token in session storage, persisting the new token response.
  // Shared by the HTTP interceptor's 401 handling, the set up your company wizard's own 401 path and
  // the Gaia assistant's token provider.
  //
  // Single-flight: Connect's refresh token is single-use, so two callers refreshing at the same
  // moment would spend the same token and all but one would fail - which ends in handleUnauthorized
  // and bounces the admin to login. That is no longer hypothetical now that the assistant refreshes
  // on its own schedule (shortly before expiry) while the interceptor still refreshes on a 401:
  // both are triggered by the same expiry and can land in the same tick. Concurrent callers share
  // one in-flight request; the field is cleared when it settles, so the next caller refreshes anew.
  //
  // Every caller must subscribe to what it gets back. The slot is filled here but only emptied by
  // `finalize`, which needs a subscription to run - so calling this and dropping the observable
  // would strand a refresh built around a refresh token read at that moment, and hand it to
  // everyone after. All three callers today subscribe immediately; nothing but this line enforces it.
  refreshAccessToken(): Observable<TokenResponse> {
    this.refreshInFlight ??= this.requestRefreshedToken().pipe(
      finalize(() => { this.refreshInFlight = null; }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    return this.refreshInFlight;
  }

  private requestRefreshedToken(): Observable<TokenResponse> {
    const str = sessionStorage.getItem(TOKEN_RESPONSE);
    const refreshToken = str ? (JSON.parse(str) as TokenResponse).refresh_token : '';
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token in session storage!'));
    }
    return this.refreshToken(refreshToken).pipe(
      tap(response => sessionStorage.setItem(TOKEN_RESPONSE, JSON.stringify(response)))
    );
  }

  getTenantId(hostname: string): string | undefined {
    const tokenStr = sessionStorage.getItem(TOKEN_RESPONSE);
    if (tokenStr) {
      const token = JSON.parse(tokenStr);
      if (this.isEnvironmentMockedWithAws(hostname) && this.hasTenantIdInMockedEnvironment(token.access_token)) {
        return this.getTenantIdFromMockedToken(token.access_token);
      } else {
        const words = CryptoJS.enc.Base64.parse(token.access_token.split('.')[1]);
        const str = CryptoJS.enc.Utf8.stringify(words);
        const obj = JSON.parse(str);
        return obj.tenant_id;
      }
    }
    return undefined;
  }

  private isEnvironmentMockedWithAws(hostname: string): boolean {
    return HOSTNAME.localhost === hostname || HOSTNAME.int === hostname;
  }

  private hasTenantIdInMockedEnvironment(token: string): boolean {
    return token.includes("tenant");
  }

  /**
   * int's Cognito appends `tenant<id>` after the signed JWT, and `tenantnone` when the token carries
   * no tenant. Read what follows the last marker rather than a fixed 36 characters: sliced, a
   * `tenantnone` token yielded the tail of the signature plus `tenantnone` as a tenant id (ODP-44320).
   */
  private getTenantIdFromMockedToken(token: string): string | undefined {
    const marker = 'tenant';
    const tenantId = token.substring(token.lastIndexOf(marker) + marker.length);
    return tenantId && tenantId !== 'none' ? tenantId : undefined;
  }

  handleTokenResponse(state: string, token: TokenResponse, hostname: string): void {
    localStorage.removeItem(VERIFIER);
    sessionStorage.removeItem(TOKEN_RESPONSE);
    sessionStorage.removeItem(SESSION_STATE);
    sessionStorage.removeItem(STATE);
    sessionStorage.setItem(TOKEN_RESPONSE, JSON.stringify(token));
    const tenantId = this.getTenantId(hostname);
    // A provisioning (non-tenant) user token has no tenant_id, but the session is still authenticated.
    const isProvisioningToken = isProvisioningTokenKind(sessionStorage.getItem(ADMIN_UI_TOKEN_KIND));
    if (tenantId || isProvisioningToken) {
      sessionStorage.setItem(SESSION_STATE, state);
    }
  }

  isAuthenticated(): boolean {
    return sessionStorage.getItem(SESSION_STATE) !== null;
  }

  /**
   * Whether the stored access token is expired, or expires within `skewMs`.
   *
   * The skew exists for consumers that must hand the token to something else before using it: a
   * token that is still valid now but expires mid-request is no use to them. Callers that only ask
   * about right now pass nothing.
   */
  isAccessTokenExpired(skewMs = 0): boolean {
    const str = sessionStorage.getItem(TOKEN_RESPONSE);
    if (!str) {
      return true;
    }
    try {
      const tr: TokenResponse = JSON.parse(str);
      const parts = tr.access_token.split('.');
      if (parts.length !== 3) {
        return true;
      }
      const base64Payload = parts[1].replaceAll('-', '+').replaceAll('_', '/');
      const paddedPayload = base64Payload.padEnd(base64Payload.length + (4 - base64Payload.length % 4) % 4, '=');
      const words = CryptoJS.enc.Base64.parse(paddedPayload);
      const payload = JSON.parse(CryptoJS.enc.Utf8.stringify(words));
      if (typeof payload.exp !== 'number' || !Number.isFinite(payload.exp)) {
        return true;
      }
      return Date.now() + skewMs >= payload.exp * 1000;
    } catch {
      return true;
    }
  }

  getUserProfile(): Observable<Profile> {
    const wkcstr = localStorage.getItem(WELL_KNOWN_CONFIG);
    if (wkcstr) {
      const wellKnownConfig: WellKnownConfig = JSON.parse(wkcstr);
      return this.httpClient.get<Profile>(wellKnownConfig.userinfo_endpoint);
    }
    throw new Error('No wellknown config in local storage!');
  }

  getUserDetailsUri(hostname: string): string {
    const config = this.domainService.getConnectIdentityConfig(hostname);
    return config.user_details_uri;
  }

  getContextResponse(page: number, limit: number, query?: string): Observable<ContextResponse> {
    let params = new HttpParams().set('page', page);
    params = params.set('size', limit);
    if (query) {
      params = params.append('search_value', query);
    }
    let url = `/v1/contexts`;
    return this.httpClient.get<ContextResponse>(url, { params: params });
  }

  getContextResponseById(contextId: string, skipSpinner?: boolean): Observable<ContextResponse> {
    let params = new HttpParams().set('page', 0);
    params = params.set('size', 1);
    params = params.set('search_value', contextId);
    if (skipSpinner) {
      params = params.set('skipSpinner', true);
    }
    let url = `/v1/contexts`;
    return this.httpClient.get<ContextResponse>(url, { params: params });
  }

  getContextFromClaims(custom: { [key: string]: string }): Context | null {
    const contextId = custom[LAST_USED_TENANT_ID];
    const name = custom[LAST_USED_TENANT_NAME];
    if (contextId !== undefined && contextId !== 'none' && name !== undefined && name !== 'none') {
      return { contextId: contextId, name: name } as Context;
    }
    return null;
  }

  showWelcomeDialog(custom: { [key: string]: string }): boolean {
    const value = custom[WELCOME_DIALOG_SEEN];
    if (!value) {
      return true;
    }
    return false;
  }

  getCurrentContextFromStorage(): Context | null {
    const str = sessionStorage.getItem(ADMIN_UI_CURRENT_CONTEXT);
    return str ? JSON.parse(str) : null;
  }

  setCurrentContextOnStorage(context: Context): void {
    sessionStorage.setItem(ADMIN_UI_CURRENT_CONTEXT, JSON.stringify(context));
  }

  removeCurrentContextOnStorage(): void {
    sessionStorage.removeItem(ADMIN_UI_CURRENT_CONTEXT);
  }

  patchCustomWithLastUsedContext(contextId: string, organizationName: string): Observable<{ [key: string]: string }> {
    const url = '/v1/me/customClaims';
    return this.httpClient.patch<any>(url, [{ op: 'add', path: '/lastUsedTenantId', value: contextId },
    { op: 'add', path: '/lastUsedTenantName', value: organizationName }]);
  }

  patchCustomWithTheme(theme: string): Observable<{ [key: string]: string }> {
    const url = '/v1/me/customClaims';
    return this.httpClient.patch<any>(url, [{ op: 'add', path: '/theme', value: theme }]);
  }

  patchCustomWithWelcomeDialogSeen(): Observable<{ [key: string]: string }> {
    const url = '/v1/me/customClaims';
    return this.httpClient.patch<any>(url, [{ op: 'add', path: '/welcomeDialogSeen', value: 'true' }]);
  }

  getTranslation(language: string): Observable<{ [key: string]: { [key: string]: string; } }> {
    const url = `/assets/i18n/${language}.json`;
    return this.httpClient.get<{ [key: string]: { [key: string]: string; } }>(url);
  }

  handleUnauthorized() {
    const hasWellKnowConfig = localStorage.getItem(WELL_KNOWN_CONFIG) !== null;
    const hostname = this.windowRef.getHostname();
    sessionStorage.clear();
    if (hasWellKnowConfig) {
      const url = this.getConnectLoginUrl(hostname, { skipTenantHint: true });
      this.windowRef.setLocationHref(url);
    } else {
      this.getWellKnownConfig(hostname).pipe(first()).subscribe(response => {
        localStorage.setItem(WELL_KNOWN_CONFIG, JSON.stringify(response));
        const url = this.getConnectLoginUrl(hostname, { skipTenantHint: true });
        this.windowRef.setLocationHref(url);
      });
    }
  }

  getAccessToken(): string | null {
    const str = sessionStorage.getItem(TOKEN_RESPONSE);
    if (str !== null) {
      const tr: TokenResponse = JSON.parse(str);
      return tr.access_token;
    }
    return null;
  }

  /**
   * The access token to hand to an embedded web component, refreshed first when it is spent.
   *
   * Both libraries that take one (the Gaia assistant and the onboarding wizard) document this as the
   * host's job. Null is their shared way of saying "no longer authenticated"; nobody is logged out
   * here, so a failed refresh costs one request rather than the session, and the next call retries.
   */
  async getValidAccessToken(): Promise<string | null> {
    if (!this.isAccessTokenExpired(EMBEDDED_TOKEN_MIN_LIFETIME_MS)) {
      return this.getAccessToken();
    }
    try {
      const response = await firstValueFrom(this.refreshAccessToken());
      return response.access_token;
    } catch {
      return null;
    }
  }

  /**
   * Whether this context needs the provisioning (non-tenant) token.
   *
   * Administrator has to be the context's ONLY role. It is carried by two different things: a
   * Kalmar-provisioned context, where it is all the user has and no tenant exists to bind a token
   * to, and a Distributor, where it sits next to the BU roles. Those BU screens call the
   * access-management API, and `provisioningScope` asks for none of its scopes - so a Distributor handed a
   * provisioning token gets 403 on the screen it lands on. Measured on stag, where a role-only test
   * said the opposite because the callback had quietly re-issued a provisioning token underneath it.
   *
   * The test is "no other role" rather than a list of the roles that need a tenant: `BU_Subscriber_
   * Terminator` is one of them and is not in PROFILE_ROLE, so a list would have to be kept in step
   * with the backend to stay correct, and would fail closed - on 403s - when it fell behind.
   */
  isProvisioningContext(context: Context): boolean {
    const roles = context.roles ?? [];
    return roles.length === 1 && roles[0] === PROFILE_ROLE.administrator;
  }

  // `contextType` comes before `taskId` because the landing path of a Distributor context is decided
  // without one: a pending onboarding task belongs to the Customer (Kalmar) Administrator. Every
  // caller that has the context passes its taskId, so a redirect never skips the onboarding.
  getRoutingPathFromRoles(roles: string[], contextType?: string, taskId?: string): string {
    // A Distributor context carries the Administrator role alongside its BU roles, so the role chain
    // below - which answers Administrator first - cannot decide this one. Inside a
    // Distributor the BU roles win, and Administrator on its own lands on the product screen.
    if (contextType === CONTEXT_TYPE.distributor) {
      if (roles.includes(PROFILE_ROLE.buAccessAdministrator)) {
        return PATH.context_distributor_user_access;
      }
      if (roles.includes(PROFILE_ROLE.buSubscriberViewer)) {
        return PATH.context_distributor_subscribers_user_access;
      }
      if (roles.includes(PROFILE_ROLE.administrator)) {
        return PATH.context_distributor_product;
      }
      // Last, so a user who also has a BU role or Administrator keeps that landing (PIODP-14).
      if (roles.includes(PROFILE_ROLE.companyMoveResponsible)) {
        return PATH.context_distributor_company_move;
      }
    }

    if (roles.includes(PROFILE_ROLE.administrator)) {
      return this.getAdministratorLandingPath(contextType, taskId);
    } else if (roles.includes(PROFILE_ROLE.subscriberAdministrator)) {
      return PATH.context_customer_user_access;
    } else if (roles.includes(PROFILE_ROLE.companyAdministrator)) {
      return PATH.context_company_user_access;
    } else if (roles.includes(PROFILE_ROLE.buAccessAdministrator)) {
      return PATH.context_distributor_user_access;
    } else if (roles.includes(PROFILE_ROLE.buSubscriberViewer)) {
      return PATH.context_distributor_subscribers_user_access;
    }
    return PATH.forbidden;
  }

  // A pending onboarding task comes first, whatever the context. Without one, a Kalmar customer's
  // Administrator lands on Organization › User (PICP-11); any other Administrator-only context
  // still has nowhere but the provisioning placeholder. Lifted out of getRoutingPathFromRoles, which
  // this branch took past Sonar's cognitive-complexity limit (S3776).
  private getAdministratorLandingPath(contextType?: string, taskId?: string): string {
    if (taskId) {
      return PATH.set_up_your_company;
    }
    return contextType === CONTEXT_TYPE.customer ? PATH.context_organization_user : PATH.provisioning;
  }

  private getBase64URLEncode(value: any): string {
    return CryptoJS.enc.Base64.stringify(value)
      .replaceAll('+', '-')
      .replaceAll('/', '_')
      .replaceAll('=', '');
  }

  private getVerifier(): string {
    const value = CryptoJS.lib.WordArray.random(32);
    return this.getBase64URLEncode(value);
  }

  private getState(): string {
    let stored = sessionStorage.getItem(STATE);
    stored ??= this.getBase64URLEncode(CryptoJS.lib.WordArray.random(16));
    return stored;
  }

  private getSha256(value: string): string {
    return this.getBase64URLEncode(CryptoJS.SHA256(value));
  }


}
